import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import {
  BufferedTextInput,
  TextInputType
} from 'record-it-components/input/TextInput/TextInput';
import { useModalOpened } from 'record-it-components/hooks/useModalOpened/useModalOpened';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import {
  SingleTabEditDialog,
  SingleTabEditDialogProps
} from 'record-it-components/modal/SingleTabEditDialog/SingleTabEditDialog';
import { prepareProps } from 'record-it-components/utils/propUtils';
import {
  MessageBox,
  MessageBoxType
} from 'record-it-components/misc/MessageBox/MessageBox';
import { LoadingOverlay } from 'record-it-components/misc/LoadingOverlay/LoadingOverlay';

import { useLoginFunction } from '../../../hooks/authentication/useLoginFunction/useLoginFunction';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { ApiRequester } from '../../../classes/ApiRequester/ApiRequester';
import { useAuth } from '../../../hooks/useAuth';

export type AutoReauthenticateDialogProps = PropsWithoutChildren<
  Omit<SingleTabEditDialogProps, 'tabTitle' | 'onAccept' | 'opened'>
>;

export const AutoReauthenticateDialog: FunctionComponent<AutoReauthenticateDialogProps> =
  React.memo((props) => {
    const { ...remainingProps } = prepareProps({
      props
    });

    const { setOpened, modalProps } = useModalOpened({
      closeOnBackDropClick: false
    });

    const { t } = useTranslation();

    const [password, setPassword] = useState('');

    const [errorMessage, setErrorMessage] = useState<string | undefined>();

    const { authenticationData } = useAuth();

    const [loading, setLoading] = useState(false);

    useEffect(() => {
      setOpened(false);
    }, [authenticationData, setOpened]);

    useEffect(() => {
      const { dispose } = ApiRequester.onAuthenticationError(() => {
        setOpened(true);
      });

      return () => {
        dispose();
      };
    }, [setOpened]);

    const handlePasswordInput = useEventCallback((pass) => {
      setPassword(pass);
    });

    const login = useLoginFunction({
      onError: (errorResponse) => {
        setErrorMessage(errorResponse.message);
      }
    });

    const currentUser = useCurrentUser();

    const handleAccept = useEventCallback(() => {
      setLoading(true);

      void login(currentUser.getRequiredCurrentUser().email, password).finally(
        () => {
          setLoading(false);
        }
      );
    });

    return (
      <SingleTabEditDialog
        {...remainingProps}
        {...modalProps}
        tabTitle={t('components.authentication.AutoReauthenticateDialog.title')}
        onAccept={handleAccept}
      >
        {errorMessage ? (
          <MessageBox type={MessageBoxType.Error}>{errorMessage}</MessageBox>
        ) : null}
        <FormGroup>
          <BufferedTextInput
            label={t('entities.User.password')}
            type={TextInputType.Password}
            value={password}
            onInput={handlePasswordInput}
            data-testid="AutoReauthenticateDialog.PasswordInput"
          ></BufferedTextInput>
        </FormGroup>
        <LoadingOverlay active={loading}></LoadingOverlay>
      </SingleTabEditDialog>
    );
  });
